import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { Card } from '@codes/ui-kit/components/ui/card';
import { FileText } from 'lucide-react';

import { useContents } from '@codes/box-contents';
import { Loader } from '@codes/shared';
import { Button, Separator } from '@codes/ui-kit';

import type { ContentView } from '@lemoncloud/eureka-contents-api';

const DISPLAY_COUNT = 10;

export const HomePage = () => {
    const { t } = useTranslation(['translation', 'box']);
    const navigate = useNavigate();
    const [displayCount, setDisplayCount] = useState(DISPLAY_COUNT);
    const { data: contentsData, isLoading } = useContents({ limit: -1, activity: true });

    const contents = useMemo(() => contentsData?.data || [], [contentsData]);
    const displayedContents = useMemo(() => {
        return contents.slice(0, displayCount);
    }, [contents, displayCount]);

    const handleContentClick = (content: ContentView) => {
        navigate(`/box/${content.id}`);
    };

    const handleViewMore = () => {
        setDisplayCount(prev => prev + DISPLAY_COUNT);
    };

    const handleViewLess = () => {
        setDisplayCount(DISPLAY_COUNT);
    };

    const showViewMore = !isLoading && displayCount < contents.length;
    const showViewLess = !isLoading && displayCount > DISPLAY_COUNT && displayCount >= contents.length;

    return (
        <div className="px-20 py-6 max-md:p-6 w-full flex justify-center max-w-screen-xl mx-auto">
            <div className="w-full">
                <div className="text-[28px] font-semibold mb-4">{t('box:home.title')}</div>
                <div className="text-text-800 mb-[10px] text-sm">{t('box:home.myPage')}</div>
                <Card className="p-2 rounded-[6px]">
                    {isLoading && <Loader />}
                    {!isLoading && contents.length === 0 && (
                        <Button
                            variant="ghost"
                            className="w-full max-w-full h-[29px] justify-start font-normal text-text-700 px-1 text-sm"
                        >
                            <span className="flex-1 text-left truncate w-0">{t('box:home.noPages')}</span>
                        </Button>
                    )}
                    {!isLoading &&
                        displayedContents.map(content => (
                            <div key={content.id}>
                                <Button
                                    variant="ghost"
                                    className="w-full h-[29px] max-w-full justify-start font-normal text-text-700 px-1 text-sm gap-2 rounded"
                                    onClick={() => handleContentClick(content)}
                                >
                                    <FileText className="h-4 w-4" />
                                    <span className="flex-1 text-left truncate w-0">{content.title}</span>
                                </Button>
                                <Separator className="my-[6px]" />
                            </div>
                        ))}
                    {(showViewMore || showViewLess) && (
                        <button
                            onClick={showViewMore ? handleViewMore : handleViewLess}
                            className="underline text-text-700 mt-[10px] ml-1 hover:text-text-800"
                        >
                            {showViewMore ? t('box:home.viewMore') : t('box:home.viewLess')}
                        </button>
                    )}
                </Card>
            </div>
        </div>
    );
};
